import React, { useContext } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import AuthContext from "../context/AuthContext";

/**
 * ✅ Helper to check if a user object is actually valid.
 * Prevents empty objects {} from counting as "logged in".
 */
const isValidUser = (user) => {
    return user && typeof user === 'object' && Object.keys(user).length > 0;
};

/**
 * 🔐 Guard for pages that REQUIRE a login (e.g., Feed, Profile, Settings)
 */
export const RequireAuth = ({ children }) => {
    const { user, loading } = useContext(AuthContext);
    const location = useLocation();

    // 1. Wait for AuthContext to finish loading
    if (loading) {
        return null;
    }

    // 2. Not Authenticated? -> Redirect to Home/Login
    if (!isValidUser(user)) {
        console.warn(`⛔ [RequireAuth] Access DENIED to ${location.pathname}. Redirecting to /`);

        // Only show toast if they tried to access a specific protected page
        if (location.pathname !== '/' && location.pathname !== '/login') {
            toast.error("Please log in to continue.", {
                toastId: "auth-error"
            });
        }

        return <Navigate to="/" replace state={{ from: location }} />;
    }

    return children;
};

/**
 * 🔄 Guard for Login/Register pages (Redirects if already logged in)
 */
export const RedirectIfAuthed = ({ children }) => {
    const { user, loading } = useContext(AuthContext);

    if (loading) return null;

    if (isValidUser(user)) {
        console.log(`↩️ [RedirectIfAuthed] User already logged in. Redirecting to /feed`);
        return <Navigate to="/feed" replace />;
    }

    return children;
};

/**
 * 🛡️ Guard for Privileged Roles (Moderator, Admin, Owner)
 * Uses a hierarchical system: Owner > Admin > Moderator > Creator > User
 */
export const RequireRole = ({ minRole = "admin", children }) => {
    const { user, loading } = useContext(AuthContext);

    if (loading) return null;

    // Must be logged in first
    if (!isValidUser(user)) {
        return <Navigate to="/" replace />;
    }

    // Define the hierarchy
    const roleOrder = ["user", "creator", "moderator", "admin", "owner"];

    // Support both user.role and user.userRole if your context structure varies
    const userRole = (user.role || user.userRole || "user").toLowerCase();
    const targetMinRole = minRole.toLowerCase();

    // Check if user's rank is high enough
    const hasPermission = roleOrder.indexOf(userRole) >= roleOrder.indexOf(targetMinRole);

    if (!hasPermission) {
        console.warn(`⛔ [RequireRole] Access DENIED. Role mismatch. Needed: ${targetMinRole}, Got: ${userRole}`);
        toast.error("You do not have permission to view this page.");
        return <Navigate to="/feed" replace />;
    }

    return children;
};